import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import { BLOG_POSTS } from '@/content/blog';

/**
 * Blog index — chronological, most recent up top. Long-tail SEO magnet.
 */
export default function BlogIndex() {
  const sorted = [...BLOG_POSTS].sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  return (
    <div className="section-light App relative">
      <Seo
        title="Blog · Meat Industry Insights"
        description="Expert articles for chefs, restaurateurs and trade buyers — MSA beef grading, Halal certification, cold-chain, export documentation and more. From the Perth trade desk that ships to Malaysia, Singapore and the UAE."
        keywords="Australian beef blog, meat industry insights, halal certification explained, MSA grading, wholesale meat blog, chef knowledge butchery"
        path="/blog"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'The Meat Works Trade Blog',
          url: 'https://themeatworks.com.au/blog',
          description: 'Long-form insights from Perth\'s premium wholesale butcher — grading, certification, cold-chain, export.',
        }}
      />
      <Navbar />

      <section className="pt-24 sm:pt-32 pb-14 bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-4">
            THE JOURNAL &middot; WHAT WE&apos;VE LEARNED
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
            Insights from
            <br />
            <span className="italic text-[var(--mw-primary)]">the trade desk.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base text-black/60 leading-relaxed">
            Long-form articles for chefs, restaurateurs and trade buyers — grading, certification,
            cold-chain, feed, and everything else that separates good meat from great.
          </p>
        </div>
      </section>

      <main className="bg-[#FAFAFA] pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-8 divide-y divide-black/[0.08]">
          {sorted.map((post, i) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group grid sm:grid-cols-[240px_1fr] gap-6 py-8 first:pt-0"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-black/5 border border-black/[0.06]">
                <img
                  src={post.heroImage}
                  alt={post.title}
                  loading={i < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                  width="240"
                  height="180"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--mw-primary)] mb-2">
                  {post.category}   &middot;   {post.readMinutes} min read
                </div>
                <h2 className="font-display text-2xl sm:text-3xl leading-tight text-black mb-2 group-hover:text-[var(--mw-primary)] transition-colors">
                  {post.title}
                </h2>
                <p className="text-sm sm:text-base text-black/60 leading-relaxed mb-3">
                  {post.excerpt}
                </p>
                <div className="text-[10px] uppercase tracking-[0.22em] text-black/40 inline-flex items-center gap-2">
                  <Clock size={11} />
                  {new Date(post.dateISO).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
